"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useListMyClubForm } from "@/hooks/useListMyClubForm";
import { useTranslations } from "next-intl";
import { ClubTypeSelector, ListMyClubFormFields } from "./ListMyClubFormFields";

interface ListMyClubDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function SuccessView({ onClose }: { onClose: () => void }) {
  const t = useTranslations("listMyClub");

  return (
    <div className="py-6 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
        <svg
          className="h-6 w-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <DialogHeader>
        <DialogTitle className="text-center">{t("successTitle")}</DialogTitle>
        <DialogDescription className="text-center mt-2">
          {t("successMessage")}
        </DialogDescription>
      </DialogHeader>
      <p className="mt-4 text-sm text-muted-foreground">
        {t("contactInfo")}{" "}
        <a
          href="https://www.spond.com/contact/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          {t("contactLink")}
        </a>
      </p>
      <Button onClick={onClose} className="mt-6">
        {t("done")}
      </Button>
    </div>
  );
}

export function ListMyClubDialog({
  open,
  onOpenChange,
}: ListMyClubDialogProps) {
  const t = useTranslations("listMyClub");
  const form = useListMyClubForm(onOpenChange);
  const isSubmitting = form.status === "submitting";

  return (
    <Dialog open={open} onOpenChange={form.handleClose}>
      <DialogContent className="sm:max-w-md">
        {form.status === "success" ? (
          <SuccessView onClose={form.handleClose} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t("title")}</DialogTitle>
              <DialogDescription>{t("description")}</DialogDescription>
            </DialogHeader>

            <form
              onSubmit={(e) =>
                form.handleSubmit(e, t("contactRequired"), t("roleRequired"))
              }
              className="space-y-4"
            >
              <ClubTypeSelector
                clubType={form.formData.clubType}
                onClubTypeChange={(v) => form.handleFieldChange("clubType", v)}
              />

              <ListMyClubFormFields
                formData={form.formData}
                isSubmitting={isSubmitting}
                hasError={form.status === "error"}
                contactError={form.contactError}
                roleError={form.roleError}
                errorMessage={form.errorMessage}
                idPrefix="list"
                onChange={form.handleChange}
                onRoleChange={(v) => form.handleFieldChange("role", v)}
                onContactPreferenceChange={(v) =>
                  form.handleFieldChange("contactPreference", v)
                }
                onReferralChange={(v) =>
                  form.handleFieldChange("referralSource", v)
                }
              />

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={form.handleClose}
                  disabled={isSubmitting}
                >
                  {t("cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t("sending") : t("submit")}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
